import { Howl, Howler } from 'howler';
import isObject from 'lodash/isObject';
import EventEmitter from 'wolfy87-eventemitter';
import createDebug from 'debug';
import { TweenMax } from 'gsap';

import Clock from './Clock';

import intensityData from '../data/halo.json';

const intensityDataCount = intensityData.length;
const debug = createDebug('app:audio-clock');

class ClockAudio extends EventEmitter {
    constructor(opts) {
        super();

        this.options = {
            clockAutoStart: true,
            duration: 0,
            loop: true,
            clock: null,
            clockServer: '/clock',
            loadHtml5: false,
            startTime: null,
            fadeSpeed: 15000,
            stopFadeSpeed: 15000,
            intensityMax: 0.3,
            ...opts,
        };

        const {
            duration,
            clock,
            clockServer,
            clockAutoStart,
            loadHtml5,
            startTime,
            count,
            sprite,
            ...soundOptions
        } = this.options;

        this.onSoundLoad = this.onSoundLoad.bind(this);
        this.onSoundPlay = this.onSoundPlay.bind(this);
        this.onSoundPause = this.onSoundPause.bind(this);
        this.onSoundSeek = this.onSoundSeek.bind(this);
        this.onSoundFade = this.onSoundFade.bind(this);
        this.onSoundEnd = this.onSoundEnd.bind(this);
        this.onAudioLoadedMetadata = this.onAudioLoadedMetadata.bind(this);
        this.onClockReady = this.onClockReady.bind(this);

        this.sound = new Howl({
            ...soundOptions,
            sprite,
        });
        this.sound.on('load', this.onSoundLoad);
        this.sound.on('play', this.onSoundPlay);
        this.sound.on('pause', this.onSoundPause);
        this.sound.on('seek', this.onSoundSeek);
        this.sound.on('fade', this.onSoundFade);
        this.sound.on('end', this.onSoundEnd);

        // this.analyserFilter = Howler.ctx.createBiquadFilter();
        // this.analyserFilter.type = 'lowpass';
        // this.analyserFilter.frequency.value = 250;

        this.analyser = Howler.ctx.createAnalyser();
        this.analyser.fftSize = 32;
        this.analyserBuffer = new Uint8Array(this.analyser.frequencyBinCount);

        // Howler.masterGain.connect(this.analyserFilter);
        // this.analyserFilter.connect(this.analyser);
        Howler.masterGain.connect(this.analyser);

        if (loadHtml5) {
            this.audio = document.createElement('audio');
            this.audio.addEventListener('loadedmetadata', this.onAudioLoadedMetadata);
            this.audio.src = soundOptions.src;
        } else {
            this.audio = null;
        }

        this.count = count;
        this.sounds = Object.keys(sprite).reduce((obj, id) => ({
            ...obj,
            [id]: [],
        }), {});
        this.stoppedSounds = [];
        this.fadeTweens = {};
        this.clockReady = false;
        this.loaded = false;
        this.soundLoaded = false;
        this.audioLoaded = false;
        this.firstSeek = false;
        this.currentSoundId = null;
        this.playing = false;
        this.duration = duration;
        this.startTime = isObject(startTime) ? startTime.valueOf() : startTime;
        this.clock = clock || new Clock({
            server: clockServer,
            autoStart: clockAutoStart,
            updateInterval: null,
        });
        this.clock.on('ready', this.onClockReady);
        this.clockSeeked = false;
    }

    onClockReady() {
        this.clockReady = true;
        this.emit('ready');
    }

    onAudioLoadedMetadata() {
        debug('HTML5 audio loaded.');

        const duration = Math.round(this.audio.duration * 1000);
        debug(`HTML5 audio duration: ${duration}`);
        if (!this.options.duration && duration > this.duration) {
            this.setDuration(duration);
        }

        this.audioLoaded = true;
        if (this.audioLoaded && this.soundLoaded) {
            this.loaded = true;
            debug('Audio loaded.');
            this.emit('load');
        }
    }

    onSoundLoad() {
        debug('Web audio loaded.');

        const { loadHtml5 } = this.options;
        const duration = Math.round(this.sound.duration() * 1000);
        debug(`Web audio duration: ${duration}`);
        if (!this.options.duration && duration > this.duration) {
            this.setDuration(duration);
        }

        this.soundLoaded = true;
        if (!loadHtml5 || (this.audioLoaded && this.soundLoaded)) {
            this.loaded = true;
            debug('Audio loaded.');
            this.emit('load');
        }
    }

    onSoundPlay(id) {
        this.playing = true;

        debug(`onPlay ${id}`);

        if (this.startTime) {
            this.seekOnClock(null, id);
        }

        // Fade in sound
        const { fadeSpeed } = this.options;
        const spriteId = this.getSpriteId(id);
        const volume = this.getVolumeBySprite(spriteId);
        this.fadeSound(id, volume, fadeSpeed);
        // this.sound.fade(this.sound.volume(id), volume, fadeSpeed, id);

        this.emit('play');
    }

    onSoundPause() {
        this.playing = false;

        debug('onPause');

        this.emit('pause');
    }

    onSoundSeek(id) {
        const time = this.sound.seek(id);

        debug(`onSeek id:${id} time:${time}`);

        this.emit('seek', time);
    }

    onSoundFade(id) {
        if (this.stoppedSounds.indexOf(id) === -1) {
            return;
        }
        debug(`onFade id:${id}`);
        this.sound.stop(id);
    }

    onSoundEnd(id) {
        this.playing = false;

        debug('onEnd');

        if (this.startTime) {
            this.seekOnClock(null, id);
        }

        this.emit('played', this.getPlayedCount());

        this.emit('end');
    }

    fadeSound(id, value, speed) {
        if (typeof this.fadeTweens[id] !== 'undefined') {
            this.fadeTweens[id].kill();
        }
        const volumeObject = {
            volume: this.sound.volume(id),
        };
        this.fadeTweens[id] = TweenMax.to(volumeObject, speed / 1000, {
            volume: value,
            onUpdate: () => {
                this.sound.volume(volumeObject.volume, id);
            },
            onComplete: () => {
                this.onSoundFade(id);
            },
        });
    }

    getIntensity() {
        const progress = this.getClockProgress();
        const index = Math.floor(intensityDataCount * progress);
        return Math.max(0, Math.min(intensityData[index] / 0.3, 1));
        // const { intensityMax } = this.options;
        // const buffer = this.analyserBuffer;
        // this.analyser.getByteFrequencyData(buffer);
        // let totalIntensity = 0;
        // let valueCount = 0;
        // buffer.forEach((val, index) => {
        //     if (index < 6) {
        //         return;
        //     }
        //     totalIntensity += val;
        //     valueCount += 1;
        // });
        // const intensity = totalIntensity / valueCount;
        // return Math.min((intensity / 256) / intensityMax, 1);
    }

    getClockProgress(time) {
        const globalProgress = this.getGlobalClockProgress(time);
        return globalProgress - Math.floor(globalProgress);
    }

    getGlobalClockProgress(time) {
        if (this.clock === null) {
            return 0;
        }
        const clockTime = typeof time !== 'undefined' && time !== null ? time : this.clock.getTime();
        return (clockTime - this.startTime) / this.duration;
    }

    getPlayedCount() {
        return Math.floor(this.getGlobalClockProgress());
    }

    getCountBySprite() {
        const count = this.count;
        const sprites = Object.keys(this.sounds).map(id => (
            parseInt(id.replace(/^count_/, ''), 10)
        )).sort().reverse();

        const countBySprites = {};

        // let currentCount = count;
        // sprites.forEach((spriteCount) => {
        //     const numberOfSprite = Math.floor(currentCount / spriteCount);
        //     if (numberOfSprite >= 1) {
        //         countBySprites[`count_${spriteCount}`] = numberOfSprite;
        //         currentCount -= (numberOfSprite * spriteCount);
        //     } else {
        //         countBySprites[`count_${spriteCount}`] = 0;
        //     }
        // });

        const minDelta = sprites.reduce((delta, spriteCount) => {
            const currentDelta = Math.abs(spriteCount - count);
            return currentDelta < delta ? currentDelta : delta;
        }, Infinity);
        const minSprite = sprites.find(spriteCount => Math.abs(spriteCount - count) === minDelta);
        sprites.forEach((spriteCount) => {
            countBySprites[`count_${spriteCount}`] = minSprite === spriteCount ? 1 : 0;
        });

        return countBySprites;
    }

    getSpriteId(soundId) {
        return Object.keys(this.sounds).reduce((current, spriteId) => (
            current === null && this.sounds[spriteId].indexOf(soundId) !== -1 ? spriteId : current
        ), null);
    }

    getVolumeBySprite(sprite) {
        const countBySprite = this.getCountBySprite();
        const spriteCount = Object.keys(countBySprite).reduce((total, spriteId) => (
            countBySprite[spriteId] > 0 ? (total + 1) : total
        ), 0);
        const soundCount = countBySprite[sprite];
        return (1 / soundCount) * (1 / spriteCount);
    }

    setCount(count) {
        debug(`Setting count to ${count}`);
        this.count = count;
        if (this.playing) {
            this.play();
        }
    }

    setDuration(duration) {
        debug(`Duration: ${duration}`);
        this.duration = duration;
    }

    startClock() {
        this.clock.start();
    }

    stopSound(id) {
        if (this.stoppedSounds.indexOf(id) !== -1) {
            return;
        }
        debug(`Stopping ${id}...`);
        const { stopFadeSpeed } = this.options;
        this.fadeSound(id, 0, stopFadeSpeed);
        this.stoppedSounds.push(id);
    }

    seekOnClock(time, id) {
        if (!this.clock.ready) {
            debug('Not ready for seeking');
            this.clock.once('ready', () => {
                this.seekOnClock(time, id);
            });
            return;
        }
        const clockProgress = this.getClockProgress(time);
        const seekTime = clockProgress * this.duration;
        debug(`Seek${typeof id !== 'undefined' ? ` ${id}` : ''} at ${seekTime}`);
        const countBySprite = this.getCountBySprite();
        Object.keys(this.sounds).forEach((spriteId) => {
            const soundsIds = this.sounds[spriteId];
            const count = countBySprite[spriteId];
            const sprite = this.options.sprite[spriteId];
            soundsIds.forEach((soundId, index) => {
                if ((typeof id !== 'undefined' && soundId === id) || index < count) {
                    this.sound.seek((sprite[0] / 1000) + (seekTime / 1000), soundId);
                    // this.sound.seek((seekTime / 1000), soundId);
                }
            });
        });
    }

    callOnSounds(method, ...args) {
        const countBySprite = this.getCountBySprite();
        Object.keys(this.sounds).forEach((spriteId) => {
            const soundsIds = this.sounds[spriteId];
            const count = countBySprite[spriteId];
            soundsIds.forEach((soundId, index) => {
                if (index < count) {
                    if (args.length > 0) {
                        this.sound[method](...args, soundId);
                    } else {
                        this.sound[method](soundId);
                    }
                } else {
                    this.stopSound(soundId);
                }
            });
        });
    }

    play() {
        const { fadeSpeed } = this.options;
        this.playing = true;
        const countBySprite = this.getCountBySprite();
        Object.keys(this.sounds).forEach((spriteId) => {
            const soundsIds = this.sounds[spriteId];
            const currentCount = soundsIds.length;
            const spriteCount = countBySprite[spriteId];
            for (let i = spriteCount; i < currentCount; i += 1) {
                this.stopSound(soundsIds[i]);
            }
            for (let i = 0; i < spriteCount; i += 1) {
                let soundId = soundsIds[i] || null;
                if (soundId === null) {
                    soundId = this.sound.play(spriteId);
                    this.sound.volume(0, soundId);
                    soundsIds.push(soundId);
                } else if (!this.sound.playing(soundId)) {
                    this.sound.play(soundId);
                    this.sound.volume(0, soundId);
                } else {
                    const volume = this.getVolumeBySprite(spriteId);
                    this.fadeSound(soundId, volume, fadeSpeed);
                    // this.sound.fade(this.sound.volume(soundId), volume, fadeSpeed, soundId);
                }
                this.stoppedSounds = this.stoppedSounds.filter(id => id !== soundId);
            }
            this.sounds[spriteId] = soundsIds;
        });
    }

    pause() {
        if (!this.playing) {
            return;
        }
        this.callOnSounds('pause');
    }

    mute() {
        this.sound.mute(true);
    }

    unmute() {
        this.sound.mute(false);
    }

    volume(volume) {
        this.callOnSounds('volume', volume);
    }

    time() {
        return this.loaded ? this.sound.seek() : 0;
    }

    clockTime() {
        if (!this.loaded || this.clock === null) {
            return 0;
        }
        const clockProgress = this.getClockProgress();
        const clockTime = clockProgress * this.duration;
        return clockTime / 1000;
    }

    getClock() {
        return this.clock;
    }

    setStartTime(time) {
        this.startTime = isObject(time) ? time.valueOf() : time;
        if (this.playing) {
            this.seekOnClock();
        }
    }

    playFromTime(time) {
        this.clockSeeked = false;
        this.startTime = isObject(time) ? time.valueOf() : time;
        if (!this.playing) {
            this.play();
        } else {
            this.seekOnClock();
        }
    }

    destroy() {
        if (this.clock) {
            this.clock.off('ready', this.onClockReady);
            this.clock.stop();
        }

        this.pause();

        Howler.masterGain.disconnect(this.analyserFilter);
        this.analyserFilter.disconnect(this.analyser);

        this.sound.off('load', this.onSoundLoad);
        this.sound.off('play', this.onSoundPlay);
        this.sound.off('pause', this.onSoundPause);
        this.sound.off('seek', this.onSoundSeek);
        this.sound.off('fade', this.onSoundFade);
        this.sound.off('end', this.onSoundEnd);
        this.sound.unload();
    }

}

export default ClockAudio;



// WEBPACK FOOTER //
// ./lib/ClockAudio.js